import {ProcessService} from './../process.service';
import {MetricsService} from './../../../shared/services/metrics.service';
import {EventEmitter, Injectable, Injector} from '@angular/core';
import {OfferWebSocketHandlerService} from './websocket/offer-web-socket-handler.service';
import {ConfigurationService} from './configuration/configuration.service';
import {ProcessRequest, ProcessRequestStatusMessage} from '../../models/process-request.model';
import {CalculationStorageService} from '../../../shared/services/calculation-storage.service';
import {AuthenticateWebSocketHandlerService} from './websocket/authenticate-web-socket-handler.service';
import {WebSocketService} from './websocket/web-socket.service';
import {Offer} from '../../models/offer/offer.model';
import {isNullOrUndefined} from 'util';
import {CustomMetricsName} from '../../../shared/components/models/metrics/custom-metrics-name.enum';
import {MetricsType} from '../../../shared/components/models/metrics/metrics-type.enum';
import {ApplicationStateService} from '../../../shared/services/application-state.service';
import {BehaviorSubject} from 'rxjs/Rx';
import {OfferMetricDataModel} from '../../../shared/components/models/metrics/offer-metric-data.model';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {CalculationData} from '../../models/calculation-data.model';
import {CalculationPolicyService} from './calculation-policy.service';
import {OfferDialogService} from './offer-dialog.service';
import {OfferValidationService} from './offer-validation.service';
import {OfferMatchingService} from './offer-matching.service';
import {DialogService} from '../dialog.service';
import {AppMode} from '../../../shared/models/app-mode.enum';
import {MissingCalculationParam} from '../../consts/missing-calculation-param.enum';
import {TranslateService} from '@ngx-translate/core';
import * as _ from 'lodash';
import {ResourceService} from '../../../shared/services/resource.service';
import {InsuranceCompanyReadonlyState} from '../../../shared/models/insurance-company-readonly-state';
import {InsuranceCompanyCode} from '../../../shared/models/insurance-company-code.enum';
import {OcDamagesDataService} from '../../../shared/services/oc-damages-data.service';
import {BusinessParamName, TechnicalParamName} from '../../../shared/models/request-offer/request-offer.enum';
import {BannersService} from '../../../shared/services/banners.service';
import {ApplicationContextService} from './configuration/application-context.service';
import {InsuranceCompanyService} from '../../../shared/services/insurance-company.service';
import {TestingPresetsService} from '../../../shared/services/debugging/testing-presets.service';
import {TestingPresetKey} from '../../enums/debugging/testing-preset-key.enum';
import {OfferResponseStatusCode} from '../../enums/offer/offer-response-status-code.enum';
import {DeviceService} from '../../../shared/services/device.service';

@Injectable()
export class OfferService {

  offers: Offer[] = [];
  $offersChange = new BehaviorSubject<Offer[]>([]);
  getOfferEvent: EventEmitter<Offer> = new EventEmitter(); // emits when offer with status GET_OFFER was found
  noOffersEvent: EventEmitter<any> = new EventEmitter(); // emits when there are no valid offers and WS is closed
  allOffersReadyEvent: EventEmitter<any> = new EventEmitter(); // emits when all offers are ready
  offerFound: Promise<boolean>;

  missingPersonalDataParams: MissingCalculationParam[];
  personalDataLogos: string[];
  registrationNumberLogos: string[];
  minOffersAmountForMixedInReadonlyBoxes = 8; // minimum amount of correct offers to show readonly boxes between offers
  offersListBannerPositions: number[] = [5, 10, 18, 30];
  offerMatched = false;
  offerDialogService: OfferDialogService;

  readonly READONLY_OFFERS_ENABLED = false;

  private timeoutForOfferWaiting: Date;
  private webSocketService: WebSocketService;
  private subscriptions = [];
  private offerMatchingEnabled;
  private readOnlyState: { [code: string]: InsuranceCompanyReadonlyState };

  get calculationId(): string {
    return this.calculationPolicy.calculationId;
  }

  get processId(): string {
    return this.processService.processId;
  }

  get wsOpenedTime(): number {
    if (this.configuration.uiConfig.wsOpenedTime) {
      return this.configuration.uiConfig.wsOpenedTime * 1000;
    }
  }

  get offersCounter(): number {
    return this.appStateService.offersState.offersCounter;
  }

  get isWaitingForOfferMode(): boolean {
    return this.appStateService.mode === AppMode.WAITING_FOR_OFFER || this.appStateService.mode === AppMode.WAITING_FOR_OFFER_LOADING;
  }

  get wsConnected(): boolean {
      return this.webSocketService ? this.webSocketService.isConnected : false;
  }

  get isEmailAddressMissing(): boolean {
    const email = this.calculationPolicy.calculationData.contact.email;
    return !isNotNullOrUndefined(email) || email === '';
  }

  constructor(private injector: Injector,
              private configuration: ConfigurationService,
              private contextService: ApplicationContextService,
              private calculationPolicy: CalculationPolicyService,
              private appStateService: ApplicationStateService,
              private storage: CalculationStorageService,
              private metricsService: MetricsService,
              private dialogService: DialogService,
              private validationService: OfferValidationService,
              private matchingService: OfferMatchingService,
              private ts: TranslateService,
              private bannersService: BannersService,
              private ocDamagesDataService: OcDamagesDataService,
              private resourceService: ResourceService,
              private processService: ProcessService,
              private insuranceCompanyService: InsuranceCompanyService,
              private deviceService: DeviceService) {
    this.setInitialState(false);
    if (this.isWaitingForOfferMode) {
      this.matchingService.init();
    }
    this.offerDialogService = new OfferDialogService(this.dialogService, this.configuration, this.appStateService, this, this.insuranceCompanyService, this.deviceService);
  }

  disconnectWS() {
    this.webSocketService.disconnect();
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  sendCalculation(calculationData?: CalculationData, isGetOffer?: boolean): Promise<any> {
    return new Promise((resolve, reject) => {
      this.resetState();
      this.ocDamagesDataService.resetData();
      this.webSocketService = this.injector.get(WebSocketService);
      let authenticateWebSocketHandlerService = this.injector.get(AuthenticateWebSocketHandlerService);
      let authenticationSubscription = authenticateWebSocketHandlerService.authObservable.subscribe(event => {
        authenticationSubscription.unsubscribe();
        if (!this.contextService.isDiagnosticStationContext) {
          this.initOffersWebSocket();
          this.setUpTimeout();
        }
        if (!isGetOffer) {
          this.calculationPolicy.postCalculation(calculationData).then(() => {
            if (!this.offerMatched && !this.contextService.isDiagnosticStationContext) {
              this.offerDialogService.initWaitingOfferDialog();
            }
            resolve();
          })
              .catch(err => {
                this.disconnectWS();
                reject(err)
              });
        }
      });
      this.subscriptions.push(authenticationSubscription);
      this.webSocketService.connect();
    });
  }

  reconnectOffersWebsocket(): void {
    this.webSocketService = this.injector.get(WebSocketService);
    let authenticateWebSocketHandlerService = this.injector.get(AuthenticateWebSocketHandlerService);
    let authenticationSubscription = authenticateWebSocketHandlerService.authObservable.subscribe(event => {
      authenticationSubscription.unsubscribe();
      if (!this.contextService.isDiagnosticStationContext) {
        this.initOffersWebSocket();
        this.setUpTimeout();
      }
    });
    this.subscriptions.push(authenticationSubscription);
    this.webSocketService.connect();
  }

  resetDialogsState(): void {
    this.offerDialogService.resetState();
  }

  resetSelectedOffer(): void {
    this.appStateService.selectedOffer = null;
  }

  getMissingPersonalDataParams(offerId: string): MissingCalculationParam[] {
    const readOnly = this.appStateService.getTechnicalParamFromOffer(offerId, TechnicalParamName.READ_ONLY);
    return readOnly ? readOnly.filter(param => param !== MissingCalculationParam.CAR_REGISTRATION_NUMBER) : [];
  }

  isEmailAddressMissingAndRequired(): boolean {
    return this.isEmailAddressMissing && this.contextService.calculationEmailRequired;
  }

  isRegistrationNumberMissing(offerId: string): boolean {
    const readOnly = this.appStateService.getTechnicalParamFromOffer(offerId, TechnicalParamName.READ_ONLY);
    return readOnly ? readOnly.includes(MissingCalculationParam.CAR_REGISTRATION_NUMBER) : false;
  }

  shouldShowOffersListBanner(offerPosition: number): boolean {
    return !!this.bannersService.offersListBannerConfig && this.offersListBannerPositions.includes(offerPosition + 1);
  }

  private initOffersWebSocket(): void {
    this.initReadonlyOffersState();
    this.offerMatchingEnabled = true;
    let offerWebSocketHandlerService: OfferWebSocketHandlerService = this.injector.get(OfferWebSocketHandlerService);
    this.appStateService.initOffersState();
    this.offerDialogService.initInsuranceCompaniesOffersState();
    this.offers = [];
    this.offerMatched = false;
    this.offerFound = new Promise(resolve => {
      this.offerDialogService.cancelMatchingOfferEvent.take(1).subscribe(() => {
        this.offerMatchingEnabled = false;
        resolve(false);
      });
      let offerSubscription = offerWebSocketHandlerService.offerObservable
        .subscribe(processRequest => {
          if (processRequest.status.code === OfferResponseStatusCode.GET_OFFER) {
            this.getOfferEvent.emit(processRequest.body.offer);
          }
          this.updateOffersState(processRequest.body.offer);
          this.processReadonlyOffer(processRequest);

          if (this.shouldBeDisplayed(processRequest)) {
            this.appStateService.addOfferHeader(processRequest.header);
            this.processOffer(processRequest.body.offer);
            if (this.isWaitingForOfferMode && this.offerMatchingEnabled) {

              const offerMatched = this.matchingService.matchOffer(processRequest.body.offer);
              if (offerMatched) {
                this.offerMatched = true;
                // offerSubscription.unsubscribe();
                this.offerDialogService.close();
                this.appStateService.mode = AppMode.WAITING_FOR_OFFER;
                this.matchingService.sendOfferMatchingMetric(true);
                resolve(true);
              }
            }
          }
        });
      if (this.isWaitingForOfferMode) {
        this.allOffersReadyEvent.subscribe(() => {
          if (!isNotNullOrUndefined(this.appStateService.selectedOffer)) { // all offers are ready & no offer was matched
            this.offerDialogService.close();
            this.disconnectWS();
            this.matchingService.sendOfferMatchingMetric(false);
            resolve(false);
          }
        });
      }
      this.subscriptions.push(offerSubscription);
    });
  }

  private resetState(): void {
    this.offers.splice(0, this.offers.length);
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  private processOffer(offer: Offer) {
    this.offers.push(offer);
    this.$offersChange.next(this.offers);
    this.offerDialogService.updateInsuranceCompanyOffersState(offer);
    this.appStateService.offersState.displayedOffersNumber = this.offers.length;
    this.storage.updateCalculation();
  }

  private processReadonlyOffer(req: ProcessRequest): void {
    const {offer} = req.body;
    const insuranceCompanyCode = <string>req.header.processContext.businessParameters.find(param => param.parameterName === BusinessParamName.INSURANCE_COMPANY_CODE).parameterValue;
    if (!this.readOnlyState.hasOwnProperty(insuranceCompanyCode)) { // init insurance company state
      this.readOnlyState[insuranceCompanyCode] = {
        totalOffersTargetAmount: offer.counter.offerTotalNumber,
        offersStatusesData: [],
        missingParams: []
      }
    }
    const readOnly = this.getTechnicalParamFromOfferRequest(req, TechnicalParamName.READ_ONLY);
    this.readOnlyState[insuranceCompanyCode].offersStatusesData.push({
      readonlyParams: (readOnly && readOnly.length) ? readOnly : [],
      statusMessage: req.status.message
    });
    this.readOnlyState[insuranceCompanyCode].missingParams = _.uniq([].concat(...this.readOnlyState[insuranceCompanyCode].offersStatusesData.map(offerStatusData => offerStatusData.readonlyParams)));
    this.missingPersonalDataParams = _.uniq([].concat(...(Object.keys(this.readOnlyState).map(key => this.readOnlyState[key].missingParams.filter(param => param !== MissingCalculationParam.CAR_REGISTRATION_NUMBER)))));
    const missingRegistrationNumber = this.readOnlyState[insuranceCompanyCode].missingParams.includes(MissingCalculationParam.CAR_REGISTRATION_NUMBER);

    // if (this.readOnlyState[insuranceCompanyCode].totalOffersTargetAmount === this.readOnlyState[insuranceCompanyCode].offersStatusesData.length) { // all offers from given insurance company are ready
      const allOffersWithError = this.readOnlyState[insuranceCompanyCode].offersStatusesData.every(offerStatusData => offerStatusData.statusMessage === 'ERROR');
      // const logoUri = offer.insuranceResource ? this.resourceService.buildResourceURL(offer.insuranceResource.insuranceLogo.logoUri) : null;
      const logoUri = this.resourceService.buildResourceURL(this.getInsuranceCompanyLogo(<InsuranceCompanyCode>insuranceCompanyCode));
      if (allOffersWithError) { // show insurance company logo in readonly box
        const insuranceCompanyMissingPersonalDataParams = this.readOnlyState[insuranceCompanyCode].missingParams.filter(param => param !== MissingCalculationParam.CAR_REGISTRATION_NUMBER);
        if (insuranceCompanyMissingPersonalDataParams.length && logoUri && !this.personalDataLogos.includes(logoUri)) {
          this.personalDataLogos.push(logoUri);
        }
        if (missingRegistrationNumber && logoUri && !this.registrationNumberLogos.includes(logoUri)) {
          this.registrationNumberLogos.push(logoUri);
        }
      } else { // at least one offer is correct - remove insurance company's logo from readonly box
        this.personalDataLogos = this.personalDataLogos.filter(logo => logo !== logoUri);
        this.registrationNumberLogos = this.registrationNumberLogos.filter(logo => logo !== logoUri);
      }
    // }
  }

  private setInitialState(clearCalculationOffer: boolean) {
    this.offers.splice(0, this.offers.length);
    if (clearCalculationOffer) {
      this.appStateService.selectedOffer = null;
    }
    this.storage.updateCalculation();
  }

  private setUpTimeout() {
    let maxWaitForOffersOnOfferScreen = this.configuration.uiConfig.maxWaitForOffersOnOfferScreen;
    this.timeoutForOfferWaiting = new Date(new Date().getTime() + (1000 * maxWaitForOffersOnOfferScreen));
  }

  private shouldBeDisplayed(processRequest: ProcessRequest): boolean {
    if (TestingPresetsService.isPresetActive(TestingPresetKey.REJECT_ALL_OFFERS)) {
      return false;
    }
    // return false; // mock
    if (processRequest.status.message === ProcessRequestStatusMessage.ERROR) {
      this.metricsService.sendSimpleMetric(CustomMetricsName.INCORRECT_OFFER, MetricsType.RESPONSE, JSON.stringify(this.getIncorrectOfferMetricValue(processRequest.body.offer)));
      return false;
    } else {
      if (!this.validationService.validateCoverages(processRequest.body.offer)) {
        return false;
      }
      const calculationId = processRequest.header.processContext.businessParameters
        .find(parameter => parameter.parameterName === BusinessParamName.CALCULATION_ID).parameterValue;

      if (this.calculationId !== calculationId) {
        return false;
      }

      let now = new Date();
      if (isNullOrUndefined(now)) {
        return true;
      }

      let nowTime = now.getTime();
      let timeoutTime = this.timeoutForOfferWaiting.getTime();
      return nowTime < timeoutTime;
    }
  }

  private getIncorrectOfferMetricValue(offer: Offer): OfferMetricDataModel {
    return {
      offerId: offer.id,
      calculationId: this.calculationId,
      processId: this.processId
    };
  }

  private updateOffersState(offer: Offer) {
    if (isNullOrUndefined(this.appStateService.offersTotalNumber)) { // init total number of offers
      this.appStateService.offersTotalNumber = offer.counter.partnerOfferTotalNumber;
    }

    // increment counter and close dialog if all offers are ready
    this.appStateService.offersState.offersCounter++;
    if (this.appStateService.allOffersReady) {
      setTimeout(() => this.allOffersReadyEvent.emit(), 1000)
    }
  }

  private initReadonlyOffersState(): void {
    this.readOnlyState = {};
    this.missingPersonalDataParams = [];
    this.personalDataLogos = [];
    this.registrationNumberLogos = [];
  }

  private getTechnicalParamFromOfferRequest(req: ProcessRequest, parameterName: TechnicalParamName): any {
    const offerTechnicalParams = req.header.technicalParameters;
    const parameter = offerTechnicalParams.find(param => param.parameterName === parameterName);
    return parameter ? parameter.parameterValue : null;
  }

  private getInsuranceCompanyLogo(code: InsuranceCompanyCode): string {
    switch (code) {
      case InsuranceCompanyCode.GENERALI:
        return 'RESOURCES/TU/GENERALI/V1/GENERALI_LOGO.png';
      case InsuranceCompanyCode.PROAMA:
        return 'RESOURCES/TU/PROAMA/V1/PROAMA_LOGO.png';
      case InsuranceCompanyCode.OMINIMO:
        return 'RESOURCES/TU/OMINIMO/V1/OMINIMO_LOGO.png';
      case InsuranceCompanyCode.LINK4:
        return 'RESOURCES/TU/LINK4/V1/LINK4_LOGO.png';
      case InsuranceCompanyCode.MTU:
        return 'RESOURCES/TU/MTU/V1/MTU24_LOGO.png';
      case InsuranceCompanyCode.BENEFIA:
        return 'RESOURCES/TU/BENEFIA/V1/BENEFIA_LOGO.png';
      case InsuranceCompanyCode.UNIQA:
        return 'RESOURCES/TU/UNIQA/V1/UNIQA_LOGO.png';
      case InsuranceCompanyCode.PEVNO:
        return 'RESOURCES/TU/PEVNO/V1/PEVNO_LOGO.png';
      case InsuranceCompanyCode.ALLIANZ:
        return 'RESOURCES/TU/ALLIANZ/V1/ALLIANZ_LOGO.png';
      case InsuranceCompanyCode.TUZ:
        return 'RESOURCES/TU/TUZ/V1/TUZ_LOGO.png';
      case InsuranceCompanyCode.BEESAFE:
        return 'RESOURCES/TU/BEESAFE/V1/BEESAFE_LOGO.png';
      case InsuranceCompanyCode.DIRECT:
        return 'RESOURCES/TU/DIRECT/V1/DIRECT_LOGO.png';
    }
  }
}
